import manifest from "../../../../config/questionnaire-condition-operators.json";
import { QUESTION_TYPE_IDS } from "./registry";

/**
 * The condition operators a visibility or routing leaf may use, by family,
 * and the question types each reads (`config/questionnaire-condition-operators.json`).
 * Python reads the same file (`apps/python-api/models/questionnaire/condition_operators.py`).
 * Every TypeScript list of operators, and every check of which question type
 * an operator may read, derives from here.
 *
 * A family's `sourceTypes` are the question types its operators read; an
 * operator that reads fewer names its own (`equals` and `not_equals` compare
 * one answer, so they do not read a multi select). Likewise `values`, how many
 * answer values a leaf of the family names (`none`, `one`, `one_or_more`), is
 * a family fact an operator may narrow (`equals`, `not_equals`, `rank_equals`:
 * exactly one). A choice operator's `polarity` says whether it holds on the
 * answers it lists (`include`) or on the others (`exclude`). Registry order is
 * the order the provider schemas list the operators. `maxGroupLeaves` is the
 * most leaf conditions one and/or group may join.
 */
type Families = typeof manifest.families;

export type ConditionOperatorFamily = keyof Families;
export type VisibilityRuleChoiceOperator = keyof Families["choice"]["operators"];
export type VisibilityRuleNumericOperator = keyof Families["numeric"]["operators"];
export type VisibilityRuleRankingOperator = keyof Families["ranking"]["operators"];
export type VisibilityRuleNotApplicableOperator =
  keyof Families["not_applicable"]["operators"];
export type VisibilityRuleOperator =
  | VisibilityRuleChoiceOperator
  | VisibilityRuleNumericOperator
  | VisibilityRuleRankingOperator
  | VisibilityRuleNotApplicableOperator;

/** How many answer values a condition leaf names. */
export type ConditionValueCount = "none" | "one" | "one_or_more";
/** Whether a choice condition holds on the answers it lists or the others. */
export type ConditionPolarity = "include" | "exclude";

interface OperatorFacts {
  sourceTypes?: ReadonlyArray<string>;
  values?: ConditionValueCount;
  polarity?: ConditionPolarity;
  /** The condition fields this operator reads, when not its family's. */
  fields?: ReadonlyArray<string>;
}

const families = manifest.families as Record<
  ConditionOperatorFamily,
  {
    sourceTypes: ReadonlyArray<string>;
    values: ConditionValueCount;
    fields: ReadonlyArray<string>;
    operators: Record<string, OperatorFacts>;
  }
>;

const VALUE_COUNTS: ReadonlyArray<string> = ["none", "one", "one_or_more"];
const POLARITIES: ReadonlyArray<string> = ["include", "exclude"];

const FAMILY_NAMES = Object.keys(families) as Array<ConditionOperatorFamily>;

function isQuestionTypeId(value: string): boolean {
  return (QUESTION_TYPE_IDS as ReadonlyArray<string>).includes(value);
}

if (
  manifest.schemaVersion !== "questionnaire-condition-operators.v1" ||
  !Number.isInteger(manifest.maxGroupLeaves) ||
  manifest.maxGroupLeaves < 1 ||
  FAMILY_NAMES.some((family) => {
    const { sourceTypes, values, operators } = families[family];
    return (
      sourceTypes.length === 0 ||
      !sourceTypes.every(isQuestionTypeId) ||
      !VALUE_COUNTS.includes(values) ||
      !families[family].fields.every((field) => manifest.conditionFields.includes(field)) ||
      Object.keys(operators).length === 0 ||
      Object.values(operators).some(
        (facts) =>
          (facts.sourceTypes !== undefined &&
            (facts.sourceTypes.length === 0 ||
              !facts.sourceTypes.every((type) => sourceTypes.includes(type)))) ||
          (facts.values !== undefined && !VALUE_COUNTS.includes(facts.values)) ||
          (facts.fields !== undefined &&
            !facts.fields.every((field) => manifest.conditionFields.includes(field))) ||
          (facts.polarity !== undefined &&
            (!POLARITIES.includes(facts.polarity) ||
              // A polarity names the family's set operator of that polarity.
              Object.values(operators).filter(
                (other) =>
                  other.polarity === facts.polarity &&
                  (other.values ?? values) === "one_or_more",
              ).length !== 1)),
      )
    );
  })
) {
  throw new TypeError(
    "Questionnaire condition-operator registry is malformed.",
  );
}

/** The most leaf conditions one compound (and/or) visibility group may join. */
export const MAX_VISIBILITY_RULE_GROUP_SIZE: number = manifest.maxGroupLeaves;
/** The fewest leaf conditions a compound group joins (`minGroupLeaves`). */
export const MIN_VISIBILITY_RULE_GROUP_SIZE: number = manifest.minGroupLeaves;

/**
 * The families as the registry writes them: the facts the generic
 * `operator_reads_source` evaluator (`rule-evaluators.ts`) reads.
 */
export const CONDITION_OPERATOR_FAMILIES: Readonly<
  Record<
    ConditionOperatorFamily,
    {
      sourceTypes: ReadonlyArray<string>;
      values: ConditionValueCount;
      fields: ReadonlyArray<string>;
      operators: Readonly<Record<string, OperatorFacts>>;
    }
  >
> = families;

/**
 * The condition fields beside `values` (`conditionFields`), and the ones a
 * grid source adds (`sourceFields`: a grid's row, and a rating grid's
 * measure). A leaf may set a field only when its operator (or its family)
 * or its source reads it: the generic `condition_fields_read` evaluator.
 */
export const CONDITION_FIELDS: ReadonlyArray<string> = manifest.conditionFields;
export const CONDITION_SOURCE_FIELDS: Readonly<Record<string, ReadonlyArray<string>>> =
  manifest.sourceFields;

function familyOperators<Family extends ConditionOperatorFamily>(
  family: Family,
): ReadonlyArray<keyof Families[Family]["operators"]> {
  return Object.freeze(
    Object.keys(families[family].operators),
  ) as ReadonlyArray<keyof Families[Family]["operators"]>;
}

export const CHOICE_CONDITION_OPERATORS = familyOperators("choice");
export const NUMERIC_CONDITION_OPERATORS = familyOperators("numeric");
export const RANKING_CONDITION_OPERATORS = familyOperators("ranking");
export const NOT_APPLICABLE_CONDITION_OPERATORS =
  familyOperators("not_applicable");

/** Every operator, in registry order (the provider schemas' order). */
export const CONDITION_OPERATORS: ReadonlyArray<VisibilityRuleOperator> =
  Object.freeze(
    FAMILY_NAMES.flatMap(
      (family) =>
        Object.keys(families[family].operators) as Array<VisibilityRuleOperator>,
    ),
  );

export function isConditionOperator(
  value: unknown,
): value is VisibilityRuleOperator {
  return (
    typeof value === "string" &&
    (CONDITION_OPERATORS as ReadonlyArray<string>).includes(value)
  );
}

/** The family an operator belongs to, or null for a value that is none. */
export function conditionOperatorFamily(
  value: unknown,
): ConditionOperatorFamily | null {
  if (typeof value !== "string") return null;
  return (
    FAMILY_NAMES.find((family) =>
      Object.prototype.hasOwnProperty.call(families[family].operators, value),
    ) ?? null
  );
}

/** The question types a family's operators read. */
export function conditionFamilyReads(
  family: ConditionOperatorFamily,
  questionType: unknown,
): boolean {
  return (
    typeof questionType === "string" &&
    families[family].sourceTypes.includes(questionType)
  );
}

/** Whether `operator` reads a question of `questionType`. */
export function conditionOperatorReads(
  operator: unknown,
  questionType: unknown,
): boolean {
  const family = conditionOperatorFamily(operator);
  if (!family || typeof questionType !== "string") return false;
  const own = families[family].operators[operator as string].sourceTypes;
  return (own ?? families[family].sourceTypes).includes(questionType);
}

function operatorFacts(
  operator: unknown,
): { family: ConditionOperatorFamily; facts: OperatorFacts } | null {
  const family = conditionOperatorFamily(operator);
  return family
    ? { family, facts: families[family].operators[operator as string] }
    : null;
}

/** How many answer values a leaf with `operator` names; null off the registry. */
export function conditionOperatorValueCount(
  operator: unknown,
): ConditionValueCount | null {
  const found = operatorFacts(operator);
  return found
    ? (found.facts.values ?? families[found.family].values)
    : null;
}

/**
 * Whether `operator` holds on the answers it lists or on the others; null for
 * an operator with no polarity (every family but choice) or none at all.
 */
export function conditionOperatorPolarity(
  operator: unknown,
): ConditionPolarity | null {
  return operatorFacts(operator)?.facts.polarity ?? null;
}

/**
 * The operator of the same polarity that takes one or more values: what an
 * `equals` / `not_equals` becomes on a multi select, whose answer is a set
 * (`any_of` / `none_of`). Null for an operator with no polarity.
 */
export function conditionMembershipOperator(
  operator: unknown,
): VisibilityRuleOperator | null {
  const found = operatorFacts(operator);
  const polarity = found?.facts.polarity;
  if (!found || !polarity) return null;
  const { values, operators } = families[found.family];
  return (
    (Object.keys(operators).find(
      (candidate) =>
        operators[candidate].polarity === polarity &&
        (operators[candidate].values ?? values) === "one_or_more",
    ) as VisibilityRuleOperator | undefined) ?? null
  );
}
